import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Typography,
  Button,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Slider,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Chip,
  Pagination,
  CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { motion } from 'framer-motion';
import axios from 'axios';

const GalleryPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const propertyId = queryParams.get('id');

  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filters, setFilters] = useState({
    rentalType: ''
  });
  const [page, setPage] = useState(1);
  const propertiesPerPage = 6;

  // Fetch all properties or redirect to property page if ID is provided
  useEffect(() => {
    const fetchProperties = async () => {
      try {
        setLoading(true);
        if (propertyId) {
          // Redirect to property page if ID is provided in URL
          navigate(`/property/${propertyId}`);
          return;
        } else {
          // Build query string based on filters
          let queryString = '';
          if (filters.rentalType) queryString += `rentalType=${filters.rentalType}&`;

          const { data } = await axios.get(`/api/properties?${queryString}`);
          setProperties(data);
        }
      } catch (error) {
        console.error('Error fetching properties:', error);
        // Use placeholder data if API fails
        setProperties([
          {
            id: '1',
            name: 'Уютный коттедж у озера',
            type: 'Коттедж',
            address: 'Тюмень, ул. Озерная, 15',
            description: 'Просторный коттедж с видом на озеро, идеально подходит для семейного отдыха. В доме 3 спальни, большая гостиная, кухня и терраса.',
            rentalType: 'daily',
            pricePerDay: 15000,
            mainImage: '/cottage1.jpg',
            PropertyImages: [
              { id: '1', imageUrl: '/cottage1.jpg', caption: 'Вид спереди' },
              { id: '2', imageUrl: '/cottage1-interior.jpg', caption: 'Гостиная' }
            ]
          },
          {
            id: '2',
            name: 'Русская баня с купелью',
            type: 'Баня',
            address: 'Тюмень, ул. Лесная, 42',
            description: 'Традиционная русская баня с купелью и комнатой отдыха. Идеальное место для релаксации и отдыха с друзьями.',
            rentalType: 'hourly',
            pricePerHour: 2500,
            mainImage: '/sauna1.jpg',
            PropertyImages: [
              { id: '3', imageUrl: '/sauna1.jpg', caption: 'Внешний вид' },
              { id: '4', imageUrl: '/sauna1-interior.jpg', caption: 'Парная' }
            ]
          },
          {
            id: '3',
            name: 'Дача в сосновом бору',
            type: 'Дача',
            address: 'Тюменский район, пос. Сосновка',
            description: 'Уютная дача в окружении соснового леса. Тихое место для спокойного отдыха на природе. На участке есть мангал и беседка.',
            rentalType: 'daily',
            pricePerDay: 8000,
            mainImage: '/dacha1.jpg',
            PropertyImages: [
              { id: '5', imageUrl: '/dacha1.jpg', caption: 'Вид с участка' },
              { id: '6', imageUrl: '/dacha1-interior.jpg', caption: 'Внутри' }
            ]
          }
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchProperties();
  }, [propertyId, filters]);

  // Handle dialog close
  const handleDialogClose = () => {
    setDialogOpen(false);
    setCurrentImageIndex(0);
    // Remove the id parameter from URL
    navigate('/gallery');
  };

  // Handle property click
  const handlePropertyClick = (property) => {
    // Redirect to property page
    navigate(`/property/${property.id}`);
  };

  // Handle image navigation
  const handleNextImage = () => {
    if (selectedProperty?.PropertyImages?.length > 0) {
      setCurrentImageIndex((prevIndex) => 
        prevIndex === selectedProperty.PropertyImages.length - 1 ? 0 : prevIndex + 1
      );
    }
  };

  const handlePrevImage = () => {
    if (selectedProperty?.PropertyImages?.length > 0) {
      setCurrentImageIndex((prevIndex) => 
        prevIndex === 0 ? selectedProperty.PropertyImages.length - 1 : prevIndex - 1
      );
    }
  };

  // Handle filter changes
  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters({
      ...filters,
      [name]: value
    });
    setPage(1); // Reset to first page when filters change
  };

  // Handle pagination
  const handlePageChange = (event, value) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Calculate pagination
  const indexOfLastProperty = page * propertiesPerPage;
  const indexOfFirstProperty = indexOfLastProperty - propertiesPerPage;
  const currentProperties = properties.slice(indexOfFirstProperty, indexOfLastProperty);
  const pageCount = Math.ceil(properties.length / propertiesPerPage);

  // Удален список типов объектов, так как фильтр по типу больше не используется

  return (
    <Box sx={{ py: 4 }}>
      <Container maxWidth="lg">
        <Typography variant="h2" component="h1" gutterBottom align="center" sx={{ mb: 4 }}>
          Галерея объектов
        </Typography>

        {/* Filters */}
        <Card sx={{ mb: 4, p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Фильтры
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="rental-type-label">Тип аренды</InputLabel>
                <Select
                  labelId="rental-type-label"
                  name="rentalType"
                  value={filters.rentalType}
                  label="Тип аренды"
                  onChange={handleFilterChange}
                >
                  <MenuItem value="">Все</MenuItem>
                  <MenuItem value="hourly">Почасовая</MenuItem>
                  <MenuItem value="daily">Посуточная</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Card>

        {/* Properties Grid */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {currentProperties.length === 0 ? (
              <Typography variant="h6" align="center" sx={{ my: 4 }}>
                Нет объектов, соответствующих выбранным фильтрам
              </Typography>
            ) : (
              <Grid container spacing={4}>
                {currentProperties.map((property) => (
                  <Grid item key={property.id} xs={12} sm={6} md={4}>
                    <motion.div
                      whileHover={{ y: -10 }}
                      transition={{ type: 'spring', stiffness: 300 }}
                    >
                      <Card 
                        sx={{ 
                          height: '100%', 
                          display: 'flex', 
                          flexDirection: 'column',
                          cursor: 'pointer',
                          transition: '0.3s',
                          '&:hover': {
                            boxShadow: '0 8px 16px rgba(0,0,0,0.1)'
                          }
                        }}
                        onClick={() => handlePropertyClick(property)}
                      >
                        <CardMedia
                          component="img"
                          height="200"
                          image={property.mainImage || '/placeholder.jpg'}
                          alt={property.name}
                        />
                        <CardContent sx={{ flexGrow: 1 }}>
                          <Typography gutterBottom variant="h5" component="h3">
                            {property.name}
                          </Typography>
                          <Box sx={{ mb: 1 }}>
                            <Chip 
                              label={property.rentalType === 'hourly' ? 'Почасовая' : 'Посуточная'} 
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{ mb: 1 }}
                            />
                          </Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {property.address}
                          </Typography>
                          <Typography variant="body2" paragraph>
                            {property.description.substring(0, 100)}...
                          </Typography>
                          <Typography variant="h6" color="primary" sx={{ mt: 2 }}>
                            {property.rentalType === 'hourly' 
                              ? `от ${Math.floor(property.pricePerHour)} ₽/час` 
                              : `от ${Math.floor(property.pricePerDay)} ₽/сутки`}
                          </Typography>
                        </CardContent>
                        <CardActions>
                          <Button size="small" onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/property/${property.id}`);
                          }}>
                            Подробнее
                          </Button>
                          <Button 
                            size="small" 
                            color="primary" 
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/booking?property=${property.id}`);
                            }}
                          >
                            Забронировать
                          </Button>
                        </CardActions>
                      </Card>
                    </motion.div>
                  </Grid>
                ))}
              </Grid>
            )}

            {/* Pagination */}
            {pageCount > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <Pagination 
                  count={pageCount} 
                  page={page} 
                  onChange={handlePageChange} 
                  color="primary" 
                  size="large"
                />
              </Box>
            )}
          </>
        )}

        {/* Property Detail Dialog */}
        <Dialog
          open={dialogOpen}
          onClose={handleDialogClose}
          maxWidth={false}
          fullWidth
          PaperProps={{
            sx: {
              width: '95vw',
              height: '85vh',
              maxWidth: '100%',
              maxHeight: '85vh',
              m: 'auto'
            }
          }}
        >
          <DialogTitle>
            {selectedProperty?.name}
            <IconButton
              aria-label="close"
              onClick={handleDialogClose}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            {selectedProperty && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Box sx={{ position: 'relative', height: 'calc(85vh - 250px)', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <CardMedia
                      component="img"
                      sx={{ 
                        maxHeight: '100%',
                        maxWidth: '100%',
                        objectFit: 'contain',
                        borderRadius: 1
                      }}
                      image={
                        selectedProperty.PropertyImages && selectedProperty.PropertyImages.length > 0
                          ? selectedProperty.PropertyImages[currentImageIndex].imageUrl
                          : selectedProperty.mainImage || '/placeholder.jpg'
                      }
                      alt={selectedProperty.name}
                    />
                    {selectedProperty.PropertyImages && selectedProperty.PropertyImages.length > 1 && (
                      <>
                        <IconButton
                          sx={{
                            position: 'absolute',
                            left: 8,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            bgcolor: 'rgba(0, 0, 0, 0.6)',
                            color: '#fff',
                            '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.8)' },
                            width: 40,
                            height: 40
                          }}
                          onClick={handlePrevImage}
                        >
                          <ArrowBackIcon />
                        </IconButton>
                        <IconButton
                          sx={{
                            position: 'absolute',
                            right: 8,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            bgcolor: 'rgba(0, 0, 0, 0.6)',
                            color: '#fff',
                            '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.8)' },
                            width: 40,
                            height: 40
                          }}
                          onClick={handleNextImage}
                        >
                          <ArrowForwardIcon />
                        </IconButton>
                        <Typography
                          sx={{
                            position: 'absolute',
                            bottom: 8,
                            left: 8,
                            bgcolor: 'rgba(0, 0, 0, 0.6)',
                            color: 'white',
                            px: 2,
                            py: 0.5,
                            borderRadius: 1
                          }}
                        >
                          {currentImageIndex + 1} / {selectedProperty.PropertyImages.length}
                        </Typography>
                      </>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12} md={8}>
                  <Typography variant="h5" gutterBottom>
                    {selectedProperty.name}
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Chip 
                      label={selectedProperty.type} 
                      sx={{ mr: 1, mb: 1 }} 
                    />
                    <Chip 
                      label={selectedProperty.rentalType === 'hourly' ? 'Почасовая' : 'Посуточная'} 
                      color="primary"
                      variant="outlined"
                      sx={{ mb: 1 }}
                    />
                  </Box>
                  <Typography variant="body1" paragraph>
                    {selectedProperty.description}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Адрес:</strong> {selectedProperty.address}
                  </Typography>
                  {selectedProperty.capacity && (
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Вместимость:</strong> {selectedProperty.capacity} человек
                    </Typography>
                  )}
                  {selectedProperty.bedrooms && (
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Спальни:</strong> {selectedProperty.bedrooms}
                    </Typography>
                  )}
                  {selectedProperty.bathrooms && (
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Ванные комнаты:</strong> {selectedProperty.bathrooms}
                    </Typography>
                  )}
                  {selectedProperty.area && (
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Площадь:</strong> {selectedProperty.area} м²
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card sx={{ p: 2, mb: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      Стоимость
                    </Typography>
                    {selectedProperty.rentalType === 'hourly' ? (
                      <Typography variant="h5" color="primary" gutterBottom>
                        {selectedProperty.pricePerHour} ₽/час
                      </Typography>
                    ) : (
                      <Typography variant="h5" color="primary" gutterBottom>
                        {selectedProperty.pricePerDay} ₽/сутки
                      </Typography>
                    )}
                    <Button
                      variant="contained"
                      color="primary"
                      fullWidth
                      onClick={() => {
                        handleDialogClose();
                        navigate(`/booking?property=${selectedProperty.id}`);
                      }}
                      sx={{ mt: 2 }}
                    >
                      Забронировать
                    </Button>
                  </Card>
                  {selectedProperty.Amenities && selectedProperty.Amenities.length > 0 && (
                    <Card sx={{ p: 2 }}>
                      <Typography variant="h6" gutterBottom>
                        Услуги и удобства
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {selectedProperty.Amenities.map((amenity) => (
                          <Chip 
                            key={amenity.id} 
                            label={amenity.name} 
                            size="small" 
                            sx={{ mb: 1 }} 
                          />
                        ))}
                      </Box>
                    </Card>
                  )}
                </Grid>
              </Grid>
            )}
          </DialogContent>
        </Dialog>
      </Container>
    </Box>
  );
};

export default GalleryPage;
